import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkUserExists, createUser } from '../services/userService';
import { useToast } from '../context/ToastContext';
import { CreateUserType } from '../types/User';
import { ROUTES } from '../constants/routes';


const CreateUserForm: React.FC = () => {
    const navigate = useNavigate();

    const [user, setUser] = useState<CreateUserType>({
        email: '',
        phone_number: '',
        first_name: '',
        last_name: '',
        password: '',
    });

    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { addToast } = useToast();



    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!user.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = 'Invalid email format';
        }

        if (!user.phone_number.match(/^\+[1-9]\d{7,14}$/)) {
            newErrors.phone_number = 'Phone number must be in international format, e.g. +1234567890';
        }

        if (!user.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }

        if (!user.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        let passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/;
        if (user.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!passwordRegex.test(user.password)) {
            newErrors.password = 'Password must contain at least one upper case letter, lower case letter, a number and a symbol.'
        }

        if (user.password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        let newValue = value;

        if (name === 'phone_number') {
            // Strip non-numeric characters except "+"
            newValue = newValue.replace(/[^\d+]/g, '');

            // Ensure it starts with "+" and only has digits after
            if (!newValue.startsWith('+1')) {
                newValue = '+1' + newValue.replace(/^\+/, '');
            }
        }

        setUser((prev) => ({ ...prev, [name]: newValue }));
    };

    const handleEmailBlur = async () => {
        const email = user.email;
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!isValid) return;

        const exists = await checkUserExists(email);
        if (exists) {
            setErrors((prev) => ({
                ...prev,
                email: 'Email already exists in the system',
            }));
        } else {
            // Clear email error if any
            setErrors((prev) => {
                const { email, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;  // Ensure validation passes

        try {
            // Step 1: Call user creation service
            const userCreationResponse = await createUser(user);  // Replace with your actual user creation service    
            if (userCreationResponse) {
                addToast('Account created successfully! Please check your email to verify your account.', false); // You can show a success toast here
                navigate(ROUTES.VERIFY_ACCOUNT + '/' + user.email);
            } else {
                throw new Error("Failure");
            }

        } catch (err) {
            console.error('Failed to create user:', err);
            addToast('There was an error creating your account. Please try again.', true);  // Show an error toast
        }
    };

    const handleCancel = () => {
        navigate(ROUTES.LOGIN);
    };

    return (
        <div style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
            <form style={{ minWidth: '300px', maxWidth: '90%', width: '600px' }} onSubmit={handleSubmit}>
                <h1>Create Account</h1>

                <div>
                    <label>Email:</label>
                    <input
                        type="email"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        required
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </div>

                <div>
                    <label>Phone:</label>
                    <input
                        type="text"
                        name="phone_number"
                        value={user.phone_number}
                        onChange={handleChange}
                        required
                        minLength={10}
                        maxLength={12}
                    />
                    {errors.phone_number && <span className="error">{errors.phone_number}</span>}
                </div>

                <div>
                    <label>First Name:</label>
                    <input
                        type="text"
                        name="first_name"
                        value={user.first_name}
                        onChange={handleChange}
                        required
                        minLength={2}
                        maxLength={100}
                    />
                    {errors.first_name && <span className="error">{errors.first_name}</span>}
                </div>

                <div>
                    <label>Last Name:</label>
                    <input
                        type="text"
                        name="last_name"
                        value={user.last_name}
                        onChange={handleChange}
                        required
                        minLength={2}
                        maxLength={100}
                    />
                    {errors.last_name && <span className="error">{errors.last_name}</span>}
                </div>

                <div>
                    <label>Password:</label>
                    <input
                        type="password"
                        name="password"
                        value={user.password}
                        onChange={handleChange}
                        required
                        minLength={8}
                        maxLength={100}
                    />
                    {errors.password && <span className="error">{errors.password}</span>}
                </div>

                <div>
                    <label>Confirm Password:</label>
                    <input
                        type="password"
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        maxLength={100}
                    />
                    {errors.confirmPassword && (
                        <span className="error">{errors.confirmPassword}</span>
                    )}
                </div>

                <div className="buttonsRow">
                    <div className="buttonsLeft">
                        <button type="button" className="cancelButton" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                    <button type="submit">Create User</button>

                </div>
            </form>
        </div>
    );
};

export default CreateUserForm;
